import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { App as CapacitorApp } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { StatusBar } from '@capacitor/status-bar'
import '@fontsource-variable/bricolage-grotesque/index.css' // Display font (brand, headings)
import '@fontsource-variable/figtree/index.css' // UI body font
import './i18n' // Initialize i18n before App
import './index.css'
import App from './App.tsx'
import { reloadOnceForStaleChunk } from './components/AppErrorBoundary'
import { initErrorReporting } from './lib/errorReporting'
import { confirmBundleStarted } from './lib/appUpdate'

initErrorReporting()

// iOS: the web view runs under the status bar and the layout pads env(safe-area-inset-top), so overlays
// (menu, dialogs) dim the whole screen. overlaysWebView: false in capacitor.config is for Android tablets.
if (Capacitor.getPlatform() === 'ios') void StatusBar.setOverlaysWebView({ overlay: true }).catch(() => undefined)

void CapacitorApp.addListener('backButton', ({ canGoBack }) => {
  if (canGoBack) {
    window.history.back();
  } else {
    void CapacitorApp.exitApp();
  }
});

// Vite emits this when preloading a code-split chunk fails — typically because
// a redeploy replaced the hashed files. Reload once to pick up the new build.
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault()
  reloadOnceForStaleChunk()
})


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      refetchOnWindowFocus: false,
    },
  },
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
)

// The bundle rendered: tell the live-update plugin, otherwise it rolls back after its readyTimeout.
void confirmBundleStarted()
